import { useState } from 'react';
import { Button, Dialog, TextField } from '@nyce/admin-kit/ui';
import { ErrorBox } from '../../components/common';
import { pods } from '../../api/pods';
import { errorMessage } from '../../lib/errors';

export interface PendingAction {
  kind: 'restart' | 'scale' | 'pause' | 'resume' | 'delete';
  environmentId: string;
  namespace: string;
  /** Deployment name, or the pod name for `delete`. */
  name: string;
  /** Current replicas (scale default). */
  replicas?: number;
}

const TITLE: Record<PendingAction['kind'], string> = {
  restart: 'Restart deployment',
  scale: 'Scale deployment',
  pause: 'Pause deployment',
  resume: 'Resume deployment',
  delete: 'Delete pod',
};

const TEXT: Record<PendingAction['kind'], string> = {
  restart: 'Rolls all pods of the deployment (rollout restart). Pods are replaced one by one.',
  scale: 'Sets the number of replicas (0–20).',
  pause: 'Remembers the current replica count and scales the deployment to 0.',
  resume: 'Scales the deployment back to the replica count it had when it was paused (or 1).',
  delete: 'Deletes the pod; its controller creates a replacement.',
};

/** Confirmation (and, for Scale, the replica count) for a pod/deployment write; the server error stays in the dialog. */
export function ActionDialog({
  action,
  onClose,
  onDone,
}: {
  action: PendingAction;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [replicas, setReplicas] = useState(String(action.replicas ?? 1));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const n = Number(replicas);
  const validScale = replicas.trim() !== '' && Number.isInteger(n) && n >= 0 && n <= 20;

  const run = async () => {
    const t = { environmentId: action.environmentId, namespace: action.namespace };
    setBusy(true);
    setError(null);
    try {
      switch (action.kind) {
        case 'restart':
          await pods.restart({ ...t, deployment: action.name });
          return onDone(`Restarting ${action.name}`);
        case 'scale':
          await pods.scale({ ...t, deployment: action.name, replicas: n });
          return onDone(`Scaled ${action.name} to ${n}`);
        case 'pause':
          await pods.pause({ ...t, deployment: action.name });
          return onDone(`Paused ${action.name}`);
        case 'resume':
          await pods.resume({ ...t, deployment: action.name });
          return onDone(`Resumed ${action.name}`);
        case 'delete':
          await pods.deletePod({ ...t, pod: action.name });
          return onDone(`Deleted pod ${action.name}`);
      }
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const danger = action.kind === 'delete' || action.kind === 'pause' || (action.kind === 'scale' && n === 0);
  return (
    <Dialog
      open
      onClose={onClose}
      title={TITLE[action.kind]}
      maxWidth={480}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            loading={busy}
            disabled={action.kind === 'scale' && !validScale}
            onClick={() => void run()}
          >
            {TITLE[action.kind].split(' ')[0]}
          </Button>
        </>
      }
    >
      <ErrorBox error={error} />
      <p className="nyk-dialog-text">
        <code>
          {action.namespace}/{action.name}
        </code>
      </p>
      <p className="nyk-dialog-text">{TEXT[action.kind]}</p>
      {action.kind === 'scale' && (
        <TextField
          label="Replicas"
          type="number"
          min={0}
          max={20}
          step={1}
          value={replicas}
          onChange={(e) => setReplicas(e.target.value)}
          error={validScale ? undefined : 'A whole number from 0 to 20'}
        />
      )}
    </Dialog>
  );
}
